/**
 * PATH: packages/email-hub/src/feature/components/charts/chartRender.tsx
 * FILE_ID: EMAILHUB-CHARTS-RENDER-001
 * PURPOSE: Library renderers for validated specs — recharts for ChartSpec
 *          (same library as the Jobs BI dashboard), React Flow + dagre for
 *          FlowSpec — plus DOM→PNG File export helpers.
 * WHY: FEAT-2026-07-03-COMPOSE-CHARTS — "don't rebuild the flowcharts in
 *      Jobs BI, reuse the libraries from there". Rendering is ALWAYS these
 *      pre-existing libraries drawing a validated spec (chartSpec.ts) —
 *      never free-form AI markup.
 * FLOW:
 *   ChartSpec ─▶ <ChartSpecView/> (recharts SVG) ─▶ chartContainerToPngFile
 *   FlowSpec  ─▶ layoutFlow (dagre) ─▶ <FlowSpecView/> (React Flow)
 *             ─▶ elementToPngFile (html-to-image)
 * DEPENDENCIES:
 *   - recharts, @xyflow/react, dagre, html-to-image: all pre-existing deps
 * RELATED:
 *   - ComposeChartModal.tsx: hosts the previews + attach/insert actions
 */
import { useMemo } from 'react'
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, Legend, Line,
  LineChart, Pie, PieChart, Tooltip, XAxis, YAxis,
} from 'recharts'
import { Background, ReactFlow, type Edge, type Node } from '@xyflow/react'
import dagre from 'dagre'
import { toBlob } from 'html-to-image'
import '@xyflow/react/dist/style.css'

import { CHART_COLORS, type ChartSpec, type FlowSpec } from './chartSpec'
import { prepareInlineImage } from '../../lib/imageClipboard'

export const CHART_PX = { width: 640, height: 360 }

/** Validated ChartSpec → recharts. Fixed size so the PNG export is stable. */
export function ChartSpecView({ spec }: { spec: ChartSpec }) {
  const { width, height } = CHART_PX
  const common = { width, height, data: spec.data }
  const axes = (
    <>
      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
      <XAxis dataKey="label" tick={{ fontSize: 12 }} />
      <YAxis tick={{ fontSize: 12 }} />
      <Tooltip />
      <Legend />
    </>
  )
  if (spec.kind === 'bar') {
    return (
      <BarChart {...common}>
        {axes}
        {spec.series.map((s, i) => (
          <Bar key={s.name} dataKey={s.name} fill={CHART_COLORS[i % CHART_COLORS.length]} />
        ))}
      </BarChart>
    )
  }
  if (spec.kind === 'line') {
    return (
      <LineChart {...common}>
        {axes}
        {spec.series.map((s, i) => (
          <Line
            key={s.name} type="monotone" dataKey={s.name} strokeWidth={2}
            stroke={CHART_COLORS[i % CHART_COLORS.length]} dot={{ r: 3 }}
          />
        ))}
      </LineChart>
    )
  }
  if (spec.kind === 'area') {
    return (
      <AreaChart {...common}>
        {axes}
        {spec.series.map((s, i) => (
          <Area
            key={s.name} type="monotone" dataKey={s.name} strokeWidth={2}
            stroke={CHART_COLORS[i % CHART_COLORS.length]}
            fill={CHART_COLORS[i % CHART_COLORS.length]} fillOpacity={0.25}
          />
        ))}
      </AreaChart>
    )
  }
  // pie — single series (validator enforces), one slice per row
  const key = spec.series[0].name
  return (
    <PieChart width={width} height={height}>
      <Tooltip />
      <Legend />
      <Pie data={spec.data} dataKey={key} nameKey="label" label outerRadius={130}>
        {spec.data.map((_, i) => (
          <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
        ))}
      </Pie>
    </PieChart>
  )
}

/** dagre top-to-bottom layout: FlowSpec → positioned React Flow nodes/edges. */
export function layoutFlow(spec: FlowSpec): { nodes: Node[]; edges: Edge[] } {
  const g = new dagre.graphlib.Graph()
  g.setGraph({ rankdir: 'TB', nodesep: 40, ranksep: 60 })
  g.setDefaultEdgeLabel(() => ({}))
  const NODE_W = 180
  const NODE_H = 44
  for (const n of spec.nodes) g.setNode(n.id, { width: NODE_W, height: NODE_H })
  for (const e of spec.edges) g.setEdge(e.from, e.to)
  dagre.layout(g)
  const nodes: Node[] = spec.nodes.map((n) => {
    const pos = g.node(n.id)
    return {
      id: n.id,
      data: { label: n.label },
      position: { x: (pos?.x ?? 0) - NODE_W / 2, y: (pos?.y ?? 0) - NODE_H / 2 },
      style: {
        width: NODE_W, fontSize: 12, borderRadius: 8,
        border: '1.5px solid #2563EB', background: '#EFF6FF', color: '#1E3A8A',
      },
    }
  })
  const edges: Edge[] = spec.edges.map((e, i) => ({
    id: `e${i}-${e.from}-${e.to}`,
    source: e.from,
    target: e.to,
    label: e.label,
    animated: false,
    style: { stroke: '#64748B', strokeWidth: 1.5 },
    labelStyle: { fontSize: 11, fill: '#334155' },
    markerEnd: { type: 'arrowclosed' as never, color: '#64748B' },
  }))
  return { nodes, edges }
}

/** Validated FlowSpec → interactive React Flow canvas (drag to tidy up). */
export function FlowSpecView({ spec }: { spec: FlowSpec }) {
  const { nodes, edges } = useMemo(() => layoutFlow(spec), [spec])
  return (
    <ReactFlow
      defaultNodes={nodes}
      defaultEdges={edges}
      fitView
      proOptions={{ hideAttribution: true }}
      nodesConnectable={false}
      deleteKeyCode={null}
    >
      <Background gap={16} color="#e5e7eb" />
    </ReactFlow>
  )
}

/** Windows-safe .png filename from a free-text base. */
export function chartFilename(base: string): string {
  const clean = base.trim().replace(/[\\/:*?"<>|]+/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 60)
  return `${clean || 'Chart'}.png`
}

/**
 * Container that holds a recharts SVG → PNG File (2x scale for crispness).
 * Serializes the <svg>, rasterises through an offscreen canvas. Rejects when
 * no SVG is present.
 */
export function chartContainerToPngFile(container: HTMLElement, filenameBase: string): Promise<File> {
  const svg = container.querySelector('svg')
  if (!svg) return Promise.reject(new Error('No chart rendered'))
  const clone = svg.cloneNode(true) as SVGSVGElement
  clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg')
  clone.style.backgroundColor = '#ffffff'
  const xml = new XMLSerializer().serializeToString(clone)
  const svgUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(xml)}`
  const width = svg.clientWidth || CHART_PX.width
  const height = svg.clientHeight || CHART_PX.height
  return new Promise<File>((resolve, reject) => {
    const img = new Image()
    img.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width = width * 2
      canvas.height = height * 2
      const ctx = canvas.getContext('2d')
      if (!ctx) { reject(new Error('Canvas unavailable')); return }
      ctx.fillStyle = '#ffffff'
      ctx.fillRect(0, 0, canvas.width, canvas.height)
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
      canvas.toBlob((blob) => {
        if (!blob) { reject(new Error('PNG encode failed')); return }
        resolve(new File([blob], chartFilename(filenameBase), { type: 'image/png' }))
      }, 'image/png')
    }
    img.onerror = () => reject(new Error('Chart rasterise failed'))
    img.src = svgUrl
  })
}

/** React Flow canvas element → PNG File (html-to-image, white background). */
export async function elementToPngFile(el: HTMLElement, filenameBase: string): Promise<File> {
  const blob = await toBlob(el, { backgroundColor: '#ffffff', pixelRatio: 2 })
  if (!blob) throw new Error('PNG encode failed')
  return new File([blob], chartFilename(filenameBase), { type: 'image/png' })
}

/** PNG File → inline <img> HTML (data URL; the send path converts inline
 *  images to real attachments). */
export function pngFileToInlineHtml(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(
      `<img src="${reader.result}" alt="${file.name.replace(/"/g, '')}" style="max-width:100%;" /><p></p>`,
    )
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(file)
  })
}

export type ChartEmailInsert =
  | { kind: 'inline'; html: string }
  | { kind: 'attach'; file: File; reason: string }

/**
 * PNG File → body insert decision, THROUGH the composer's inline-image
 * guard (BUG-2026-07-06-CHART-INSERT-BYPASSES-INLINE-GUARD). The raw
 * pngFileToInlineHtml path embedded any size data-URL: over the 4 MB
 * send-path cap the recipient got a stripped/broken image, and huge
 * data-URLs pushed the cross-device draft-save JSON past the backend
 * body limit (the inserted chart vanished on resume). prepareInlineImage
 * downscales and falls back to "attach" — the same contract paste/drop
 * images honour, so a chart is always delivered.
 */
export async function pngFileToEmailInsert(file: File): Promise<ChartEmailInsert> {
  const prepared = await prepareInlineImage(file)
  if (prepared.kind === 'attach') return prepared
  const alt = file.name.replace(/"/g, '')
  return {
    kind: 'inline',
    html: `<img src="${prepared.dataUrl}" alt="${alt}" style="max-width:100%;" /><p></p>`,
  }
}
